import {
  createAppointmentServicesDraft,
  type AppointmentServicesDraftFailureReason,
  type AppointmentServicesDraftGroupGuestInput,
  type CreateAppointmentServicesDraftResult,
} from "@/app/booking/adapters/appointmentServicesDraft";
import type { AppointmentDraftMode } from "@/app/booking/core/appointmentBookingDraft";
import { isSpecialistStepOneFeasible } from "@/app/booking/core/specialistStepOneFeasibility";
import type {
  SpecialistAssignment,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";

export type SpecialistServicesAppointmentDraftFailureReason =
  AppointmentServicesDraftFailureReason;

export type CreateSpecialistServicesAppointmentDraftResult =
  CreateAppointmentServicesDraftResult;

export type SpecialistServicesAppointmentGroupGuestInput =
  AppointmentServicesDraftGroupGuestInput;

export interface CreateSpecialistServicesAppointmentDraftInput {
  draftId: string;
  mode: AppointmentDraftMode;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
  selections: readonly SpecialistServiceSelection[];
  groupGuests?: readonly SpecialistServicesAppointmentGroupGuestInput[];
}

/**
 * Specialist-owned Step-1 boundary. All catalog/configuration work is shared
 * with Location origin; this adapter supplies only the exact route assignment
 * and therefore locks every valid task to `specialist-entry` staff.
 */
export function createSpecialistServicesAppointmentDraft({
  assignment,
  ...input
}: CreateSpecialistServicesAppointmentDraftInput): CreateSpecialistServicesAppointmentDraftResult {
  if (input.mode === "group") {
    return { ok: false, reason: "group-booking-unavailable" };
  }

  const selectedServiceIds = new Set([
    ...input.selections.map(({ serviceId }) => serviceId),
    ...(input.groupGuests ?? []).flatMap(({ selections }) =>
      selections.map(({ serviceId }) => serviceId),
    ),
  ]);
  const owner = {
    locationId: assignment.locationId,
    userLocationId: assignment.userLocationId,
    catalogVersion: input.workplace.catalogVersion,
  };

  if (
    input.services.some(
      (service) =>
        selectedServiceIds.has(service.serviceId) &&
        !isSpecialistStepOneFeasible({
          branch: "service",
          owner,
          service,
        }),
    )
  ) {
    return { ok: false, reason: "invalid-selection" };
  }

  return createAppointmentServicesDraft({
    ...input,
    origin: {
      kind: "specialist-services",
      assignment,
    },
  });
}
